import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, History, HelpCircle, Trash2, Undo2 } from "lucide-react";
import { AttachControls, AttachmentList, PD_REMINDER } from "@/components/Attachments";
import { StatusDot, STATUS_LABEL, statusOf } from "@/components/StatusDot";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { type Attachment, type Section, useBriefActions, useRevisions } from "@/lib/briefs";

const CONTENT_LIMIT = 2000;
const AUTOSAVE_MS = 800;

/**
 * The upper half of the workspace: the box being written, or, with nothing
 * picked, the whole brief at a glance. One box at a time, so the page has one
 * place to write and one place to talk.
 */
export function SectionPanel({
  briefId,
  sections,
  attachments,
  shown,
  pinned,
  canEdit,
  onPick,
}: {
  briefId: string;
  sections: Section[];
  attachments: Attachment[];
  shown: Section | null;
  /** True when the person chose this box; false when the panel is following the AI. */
  pinned: boolean;
  canEdit: boolean;
  onPick: (id: string | null) => void;
}) {
  if (!shown) {
    return <Overview sections={sections} attachments={attachments} briefId={briefId} canEdit={canEdit} onPick={onPick} />;
  }
  return (
    <Box
      key={shown.id}
      briefId={briefId}
      section={shown}
      index={sections.findIndex((s) => s.id === shown.id)}
      attachments={attachments.filter((a) => a.sectionId === shown.id)}
      pinned={pinned}
      canEdit={canEdit}
      onOverview={() => onPick(null)}
    />
  );
}

function Overview({
  sections,
  attachments,
  briefId,
  canEdit,
  onPick,
}: {
  sections: Section[];
  attachments: Attachment[];
  briefId: string;
  canEdit: boolean;
  onPick: (id: string | null) => void;
}) {
  const actions = useBriefActions(briefId);
  const loose = attachments.filter((a) => a.sectionId === null || !sections.some((s) => s.id === a.sectionId));
  return (
    <section className="bg-card border-border rounded-lg border p-4" aria-label="Overview">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">The brief at a glance</h2>
        <p className="text-muted-foreground text-xs">{canEdit ? "Pick a box to work on it, or just answer below." : "Pick a box to read it."}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {sections.map((s, i) => {
          const status = statusOf(s);
          const text = s.content.trim();
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s.id)}
              className={cn(
                "hover:bg-accent/50 rounded-md border px-3 py-2 text-left transition-colors",
                s.star ? "border-highlight" : "border-border",
              )}
            >
              <div className="flex items-center gap-2">
                <StatusDot status={status} />
                <span className="min-w-0 truncate text-sm font-medium">
                  <span className="text-muted-foreground tabular-nums">{i + 1}</span> {s.title}
                  {s.star ? <span className="text-star ml-1">★</span> : null}
                </span>
                <span className="text-muted-foreground ml-auto shrink-0 text-[11px]">{STATUS_LABEL[status].split(",")[0]}</span>
              </div>
              <p className={cn("mt-1 line-clamp-2 text-xs", text ? "text-foreground/80" : "text-muted-foreground italic")}>
                {text || (s.state === "not_sure" ? "Not sure yet." : "Nothing yet.")}
              </p>
            </button>
          );
        })}
      </div>
      {/* On a phone the rail is a strip, so the brief's files live here instead. */}
      <div className="mt-3 lg:hidden">
        <div className="text-muted-foreground flex items-center justify-between text-xs font-medium">
          <span>Files for the whole brief</span>
          {canEdit ? (
            <AttachControls briefId={briefId} sectionId={null} compact busy={actions.upload.isPending} onPick={(file, sectionId) => actions.upload.mutate({ file, sectionId })} />
          ) : null}
        </div>
        {loose.length === 0 && canEdit ? <p className="text-muted-foreground mt-1 text-[11px]">{PD_REMINDER}</p> : null}
        <AttachmentList briefId={briefId} items={loose} canEdit={canEdit} busy={actions.deleteAttachment.isPending} onDelete={(id) => actions.deleteAttachment.mutate(id)} />
      </div>
    </section>
  );
}

/**
 * One box. Content autosaves ~0.8 s after typing stops, on blur, and when
 * the panel moves to another box; every save is a revision. The status line
 * is the assessor's advisory verdict; "not sure" is the person's own answer.
 */
function Box({
  briefId,
  section,
  index,
  attachments,
  pinned,
  canEdit,
  onOverview,
}: {
  briefId: string;
  section: Section;
  index: number;
  attachments: Attachment[];
  pinned: boolean;
  canEdit: boolean;
  onOverview: () => void;
}) {
  const actions = useBriefActions(briefId);
  const [text, setText] = useState(section.content);
  const [dirty, setDirty] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [flash, setFlash] = useState(false);
  const timer = useRef<number | null>(null);
  const latest = useRef({ text, dirty });
  latest.current = { text, dirty };
  const area = useRef<HTMLTextAreaElement>(null);

  // The server text the person's unsaved draft started from, and whether the
  // AI wrote over it in a way the draft could not take in.
  const base = useRef(section.content);
  const [aiAlso, setAiAlso] = useState(false);
  const saveRef = useRef<(value: string) => void>(() => {});
  const schedule = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => saveRef.current(latest.current.text), AUTOSAVE_MS);
  };

  // Server-side changes (an AI write, an undo) replace local text unless the
  // person is mid-edit. Mid-edit, an AI write is never quietly lost to the next
  // autosave: an addition (the box as it was, plus new text) is merged into the
  // draft and saved with it; anything else leaves the draft as typed and says
  // the AI also wrote here, with History a tap away. The person's own save
  // coming back is not an AI write. An AI write also lights the box up.
  useEffect(() => {
    if (!latest.current.dirty) {
      base.current = section.content;
      setText(section.content);
      return;
    }
    if (section.content === base.current) return;
    const was = base.current.trimEnd();
    base.current = section.content;
    if (section.updatedBy !== "ai") return;
    const draft = latest.current.text;
    if (section.content.startsWith(was)) {
      const added = section.content.slice(was.length).trim();
      if (added && !draft.includes(added)) {
        const merged = draft.trimEnd() ? `${draft.trimEnd()}\n${added}` : added;
        latest.current = { text: merged, dirty: true };
        setText(merged);
        schedule();
      }
      return;
    }
    setAiAlso(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.content, section.updatedBy, dirty]);
  useEffect(() => {
    if (section.updatedBy !== "ai") return;
    setFlash(true);
    const t = window.setTimeout(() => setFlash(false), 1600);
    return () => window.clearTimeout(t);
  }, [section.updatedAt, section.updatedBy]);

  // The box grows with its text, up to a point, so the chat keeps its room.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 76), 320)}px`;
  }, [text]);

  const save = (value: string) => {
    if (value === section.content) {
      setDirty(false);
      return;
    }
    // Still dirty if the person (or a merge) changed the draft while this saved.
    actions.patchSection.mutate({ sectionId: section.id, content: value }, { onSettled: () => latest.current.text === value && setDirty(false) });
  };
  saveRef.current = save;
  const onChange = (value: string) => {
    latest.current = { text: value, dirty: true };
    setText(value);
    setDirty(true);
    schedule();
  };
  const onBlur = () => {
    if (timer.current) window.clearTimeout(timer.current);
    if (dirty) save(text);
  };
  // Moving to another box must not lose what was typed in this one.
  const patch = actions.patchSection;
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (latest.current.dirty && latest.current.text !== section.content) {
        patch.mutate({ sectionId: section.id, content: latest.current.text });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const status = statusOf(section);
  const saving = actions.patchSection.isPending;
  const revisions = useRevisions(briefId, showHistory ? section.id : null);
  const needsMore = section.assessment && !section.assessmentStale && section.assessment.status === "needs_more" ? section.assessment : null;

  return (
    <section
      aria-label={section.title}
      className={cn(
        "bg-card rounded-lg border p-4 transition-shadow",
        section.star ? "border-highlight" : "border-border",
        flash ? "ring-ring ring-2 ring-offset-2 ring-offset-background" : "",
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs tabular-nums">{index + 1}</span>
            <h2 className="truncate text-sm font-semibold">
              {section.title}
              {section.star ? (
                <span className="text-star ml-1" title="Matters most">
                  ★
                </span>
              ) : null}
            </h2>
            <span className="flex items-center gap-1.5 text-xs" title={STATUS_LABEL[status]}>
              <StatusDot status={status} />
              <span className="text-muted-foreground hidden sm:inline">{STATUS_LABEL[status]}</span>
            </span>
          </div>
          {section.guidingQuestion ? <p className="text-muted-foreground mt-0.5 text-xs">{section.guidingQuestion}</p> : null}
        </div>
        <span className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
          {!pinned ? <span title="This box came up because the AI just wrote it. Pick a box on the left to stay on it.">Following the AI</span> : null}
          <button type="button" onClick={onOverview} className="hover:text-foreground hover:underline">
            Overview
          </button>
        </span>
      </header>

      {needsMore ? (
        <p className="text-status-needs-more mt-2 text-xs">
          {needsMore.note}
          {needsMore.missing.length > 0 ? ` Missing: ${needsMore.missing.join(", ")}.` : ""}
        </p>
      ) : null}

      {canEdit && aiAlso ? (
        <p role="status" className="text-muted-foreground mt-2 text-xs">
          The AI also wrote here —{" "}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {
              setAiAlso(false);
              setShowHistory(true);
            }}
          >
            Review
          </button>
        </p>
      ) : null}

      {canEdit ? (
        <textarea
          ref={area}
          value={text}
          maxLength={CONTENT_LIMIT}
          placeholder={section.state === "not_sure" ? "Not sure yet, and that's fine. Add anything that comes to mind." : "Nothing here yet. Answer the question below and the AI writes it here, or type straight in."}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-label={section.title}
          className="border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-ring mt-3 w-full resize-none rounded-md border px-3 py-2 text-sm leading-relaxed focus-visible:outline-2"
        />
      ) : (
        <p className={cn("mt-3 text-sm whitespace-pre-wrap", !section.content.trim() && "text-muted-foreground italic")}>
          {section.content.trim() || (section.state === "not_sure" ? "Not sure yet." : "Left blank.")}
        </p>
      )}

      <footer className="mt-2 flex flex-wrap items-center gap-1.5">
        {canEdit ? (
          <>
            <Button
              variant={section.state === "not_sure" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => actions.patchSection.mutate({ sectionId: section.id, state: section.state === "not_sure" ? "draft" : "not_sure" })}
            >
              <HelpCircle size={14} /> {section.state === "not_sure" ? "Not sure" : "Mark not sure"}
            </Button>
            {text.trim() && section.state !== "done" ? (
              <Button variant="ghost" size="sm" onClick={() => actions.patchSection.mutate({ sectionId: section.id, state: "done" })}>
                <Check size={14} /> Done
              </Button>
            ) : null}
            {section.updatedBy === "ai" ? (
              <>
                <Button variant="secondary" size="sm" onClick={() => actions.keep.mutate(section.id)}>
                  <Check size={14} /> Keep
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowHistory(true)}>
                  <Undo2 size={14} /> Undo
                </Button>
              </>
            ) : null}
            <AttachControls briefId={briefId} sectionId={section.id} busy={actions.upload.isPending} compact onPick={(file, sectionId) => actions.upload.mutate({ file, sectionId })} />
            <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)} aria-label="History">
              <History size={14} />
            </Button>
            {section.coreKey === null ? (
              <Button
                variant="ghost"
                size="sm"
                className="hover:text-destructive"
                aria-label="Remove section"
                onClick={() => {
                  if (window.confirm(`Remove the section "${section.title}"?`)) actions.deleteSection.mutate(section.id, { onSuccess: onOverview });
                }}
              >
                <Trash2 size={14} />
              </Button>
            ) : null}
          </>
        ) : null}
        <span className="text-muted-foreground ml-auto text-[11px]">{saving ? "Saving…" : dirty ? "Unsaved" : `${text.length}/${CONTENT_LIMIT}`}</span>
      </footer>

      <AttachmentList briefId={briefId} items={attachments} canEdit={canEdit} busy={actions.deleteAttachment.isPending} onDelete={(id) => actions.deleteAttachment.mutate(id)} />
      {actions.upload.isError ? <p className="text-destructive mt-1 text-xs">{(actions.upload.error as Error).message}</p> : null}

      {showHistory ? (
        <div className="bg-secondary/50 mt-3 rounded-md p-2 text-xs">
          <div className="text-muted-foreground mb-1 font-medium">Earlier versions of this box</div>
          {revisions.isPending ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : (revisions.data ?? []).length === 0 ? (
            <p className="text-muted-foreground">No changes yet.</p>
          ) : (
            <ul className="space-y-1">
              {(revisions.data ?? []).map((r) => (
                <li key={r.id} className="flex items-start gap-2">
                  <span className="text-muted-foreground w-10 shrink-0">{r.author === "ai" ? "AI" : "You"}</span>
                  <span className="min-w-0 flex-1 truncate">{r.content.trim() || "(blank)"}</span>
                  {r.content !== section.content ? (
                    <button
                      type="button"
                      className="text-primary shrink-0 hover:underline"
                      onClick={() => actions.restoreRevision.mutate({ sectionId: section.id, revisionId: r.id }, { onSuccess: () => setShowHistory(false) })}
                    >
                      Restore
                    </button>
                  ) : (
                    <span className="text-muted-foreground shrink-0">current</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}
